/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { Form as FinalForm, Field } from 'react-final-form'
import { FieldValidator } from 'final-form'
import { useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import { Typography } from '@mui/material'
import SignInInput from 'components/Auth/SignInInput'
import SignInSwitch from 'components/Auth/SignInSwitch'
import { validateRequired, validateEmail } from 'utils/validation'
import { cmsRoutes } from 'routes/cmsRoutes'
import { useAuthContext } from 'utils/auth'
import { ReactComponent as EmailIcon } from 'assets/icons/email.svg'
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg'
import { ReactComponent as OpenEyeIcon } from 'assets/icons/open-eye.svg'
import { ReactComponent as CloseEyeIcon } from 'assets/icons/close-eye.svg'
import { REMEMBER_ME } from 'constants/consts'
import * as classes from './styles'

const NOT_AUTHORIZED_EXCEPTION = 'NotAuthorizedException'
const INVALID_CREDENTIALS = 'Invalid username or password.'
const SUBMIT_BUTTON_CONTENT = 'Login'

type SignInParameters = {
  mail: string
  password: string
  rememberMe: boolean
}

const initialValues: SignInParameters = {
  mail: '',
  password: '',
  rememberMe: true,
}

function composeValidators<FieldValue>(...validators: FieldValidator<FieldValue>[]) {
  return (value: FieldValue, allValues: object) =>
    validators.reduce((error, validator) => error || validator(value, allValues), undefined)
}

function Form() {
  const [isLoading, setIsLoading] = useState(false)
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const { signIn } = useAuthContext()

  const handleSignIn = async (values: SignInParameters) => {
    setIsLoading(true)

    try {
      await signIn(values.mail, values.password)

      localStorage.setItem(REMEMBER_ME, JSON.stringify(!!values.rememberMe))
      navigate(cmsRoutes.ROOT)
    } catch (e) {
      const error = e as Error

      if (error.name === NOT_AUTHORIZED_EXCEPTION) {
        setError(INVALID_CREDENTIALS)
      } else {
        setError(error.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordShow = () => {
    setIsPasswordShown((isShown) => !isShown)
  }

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={handleSignIn}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} css={classes.formEl}>
          <Field
            type="mail"
            name="mail"
            label="Email"
            placeholder="Email"
            component={SignInInput}
            validate={composeValidators(validateRequired, validateEmail)}
            startAdornment={
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            }
          />
          <Field
            type={isPasswordShown ? 'text' : 'password'}
            name="password"
            label="Password"
            placeholder="Password"
            component={SignInInput}
            validate={validateRequired}
            startAdornment={
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handlePasswordShow}
                  edge="end"
                  disableRipple
                  disableFocusRipple
                >
                  {isPasswordShown ? <CloseEyeIcon /> : <OpenEyeIcon />}
                </IconButton>
              </InputAdornment>
            }
          />
          {error && (
            <Typography variant="caption" css={classes.error}>
              {error}
            </Typography>
          )}
          <Field type="checkbox" name="rememberMe" component={SignInSwitch} />

          <Button color="blue" type="submit" size="large" disabled={isLoading} fullWidth css={classes.submitButton}>
            {SUBMIT_BUTTON_CONTENT}
          </Button>
        </form>
      )}
    />
  )
}

export default Form
